import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ClipboardDocumentListIcon,
    PlusCircleIcon,
    MagnifyingGlassIcon,
    ArrowUpOnSquareStackIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA ---
const initialJobWorkData = [
    {
        id: 1,
        jobTitle: 'Website Redesign Project',
        date: '2025-09-20',
        hours: 6,
        description: 'Completed the new homepage and contact page layouts. Started on the blog page.',
        employee: 'Aisha Sharma'
    },
    {
        id: 2,
        jobTitle: 'Marketing Campaign Launch',
        date: '2025-09-18',
        hours: 4,
        description: 'Prepared social media assets and scheduled posts for the new product.',
        employee: 'Priya Singh'
    },
    {
        id: 3,
        jobTitle: 'Client Meeting & Presentation',
        date: '2025-09-15',
        hours: 2.5,
        description: 'Met with the client to present the Q3 performance report and discuss new goals.',
        employee: 'Amit Patel'
    },
];
// --- END: MOCK DATA ---

const JobWorkReport = () => {
    const [jobWorkEntries, setJobWorkEntries] = useState(initialJobWorkData);
    const [newEntry, setNewEntry] = useState({
        jobTitle: '',
        date: moment().format('YYYY-MM-DD'),
        hours: '',
        description: '',
    });
    const [searchTerm, setSearchTerm] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewEntry(prevState => ({ ...prevState, [name]: value }));
    };

    const handleAddEntry = (e) => {
        e.preventDefault();
        if (newEntry.jobTitle && newEntry.hours) {
            const entry = {
                id: Date.now(),
                ...newEntry,
                employee: 'John Doe', // Hardcoded for this example
            };
            setJobWorkEntries(prevEntries => [entry, ...prevEntries]);
            setNewEntry({
                jobTitle: '',
                date: moment().format('YYYY-MM-DD'),
                hours: '',
                description: '',
            });
        }
    };

    const filteredEntries = jobWorkEntries.filter(entry =>
        entry.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.employee.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-sky-200 p-8 text-gray-800">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-4xl font-extrabold text-indigo-800 flex items-center">
                        <ClipboardDocumentListIcon className="h-10 w-10 text-indigo-600 mr-4" />
                        Job Work Entry & Report
                    </h1>
                </div>

                {/* Job Work Entry Form */}
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={cardVariants}
                    className="bg-white p-6 rounded-3xl shadow-lg mb-8"
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                        <PlusCircleIcon className="h-6 w-6 text-gray-500 mr-2" />
                        Log New Job Work
                    </h2>
                    <form onSubmit={handleAddEntry} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-1">Job Title</label>
                            <input
                                type="text"
                                name="jobTitle"
                                value={newEntry.jobTitle}
                                onChange={handleInputChange}
                                placeholder="e.g., Project Alpha Design"
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-1">Hours Spent</label>
                            <input
                                type="number"
                                name="hours"
                                value={newEntry.hours}
                                onChange={handleInputChange}
                                placeholder="e.g., 5.5"
                                step="0.5"
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                required
                            />
                        </div>
                        <div className="col-span-1 md:col-span-2">
                            <label className="block text-gray-700 font-semibold mb-1">Work Description</label>
                            <textarea
                                name="description"
                                value={newEntry.description}
                                onChange={handleInputChange}
                                placeholder="Detailed summary of work completed..."
                                rows="3"
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                        </div>
                        <div className="col-span-1 md:col-span-2 flex justify-end">
                            <motion.button
                                type="submit"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl shadow-lg hover:bg-blue-700 transition-colors"
                            >
                                Submit Work
                            </motion.button>
                        </div>
                    </form>
                </motion.div>

                {/* Job Work Report Table */}
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <ArrowUpOnSquareStackIcon className="h-6 w-6 text-gray-500 mr-2" />
                            Job Work Report
                        </h2>
                        <div className="relative mb-4">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                                <MagnifyingGlassIcon className="h-5 w-5" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search by job title or employee name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 pl-10 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence>
                                    {filteredEntries.length > 0 ? (
                                        filteredEntries.map(entry => (
                                            <motion.tr
                                                key={entry.id}
                                                variants={cardVariants}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.jobTitle}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(entry.date).format('MMMM D, YYYY')}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.hours}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entry.employee}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{entry.description}</td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No job work entries found.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default JobWorkReport;